// metricBasis.ts — KANONICZNY KONTRAKT METRYK Control Center.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  PO CO TO ISTNIEJE
//
//  Analiza parytetu wykazała, że stary panel i Control Center liczą co innego w SZEŚCIU
//  zdolnościach — i że rozjazd jest NIEJEDNORODNY: dla szkół z promocją Control Center pokazuje
//  ok. 1,49× wartości panelu, dla szkół bez promocji dokładnie tyle samo. To znaczy, że
//  podmiana podstawy kwoty zmienia nie poziom liczb, tylko RANKING szkół — a na rankingu stoją
//  rekomendacje wyjazdowe.
//
//  Wniosek: liczba bez podanej podstawy jest w tym produkcie bezużyteczna, a czasem szkodliwa.
//  Dlatego każda metryka pokazana operatorowi niesie ze sobą trzy wymiary:
//      • CO liczymy w pieniądzu   (wartość katalogowa czy kwota pobrana)
//      • CO liczymy w wolumenie   (zamówienia, porcje, kupujący, wiersze pozycji)
//      • PO JAKIEJ OSI CZASU      (dzień dostawy czy dzień zakupu)
//
//  Decyzje: ADR-001 (pieniądz), ADR-002 (wolumen), ADR-003 (oś czasu).
// ══════════════════════════════════════════════════════════════════════════════════════════════

/** Podstawa kwoty. Dwie różne metryki, nie dwie wersje tej samej. */
export type ValueMetric =
  /** Suma cen katalogowych pozycji — wartość oferty PRZED zniżką szkolną. */
  | 'LIST_VALUE'
  /** Kwota faktycznie pobrana. Dostępna WYŁĄCZNIE na ziarnie, na którym źródło ją ma. */
  | 'PAID_AMOUNT';

/**
 * Jednostka wolumenu. Zakaz używania ogólnego „wolumen" — każda z tych czterech odpowiada
 * na inne pytanie i mylenie ich było realnym defektem starego panelu (liczył wiersze pozycji
 * i nazywał je transakcjami).
 */
export type VolumeUnit =
  /** Liczba różnych zamówień (deduplikacja po identyfikatorze zamówienia). */
  | 'ORDER_COUNT'
  /** Suma ilości z pozycji — ile porcji realnie poszło. */
  | 'PORTION_COUNT'
  /** Liczba różnych kupujących. */
  | 'UNIQUE_BUYERS'
  /** Liczba wierszy pozycji zamówienia. NIE jest liczbą zamówień ani liczbą porcji. */
  | 'ORDER_ITEM_ROWS';

/**
 * Oś czasu. Panel legacy jest tu wewnętrznie niespójny — okno tnie po dacie dostawy,
 * a wykres kubełkuje po dacie zakupu. Control Center tej niespójności NIE kopiuje.
 */
export type TimeAxis =
  /** Dzień realizacji. Kanoniczna oś wyników biznesowych (ADR-003). */
  | 'DELIVERY_DATE'
  /** Dzień złożenia zamówienia. Osobny wymiar — „kiedy ludzie zamawiają", nie „ile sprzedano". */
  | 'ORDER_DATE';

export interface MetricBasis {
  value?: ValueMetric;
  volume?: VolumeUnit;
  time?: TimeAxis;
}

export const VALUE_METRIC_LABEL: Record<ValueMetric, string> = {
  LIST_VALUE: 'ceny katalogowe',
  PAID_AMOUNT: 'kwota pobrana',
};

export const VOLUME_UNIT_LABEL: Record<VolumeUnit, string> = {
  ORDER_COUNT: 'zamówienia',
  PORTION_COUNT: 'porcje',
  UNIQUE_BUYERS: 'kupujący',
  ORDER_ITEM_ROWS: 'wiersze pozycji',
};

export const TIME_AXIS_LABEL: Record<TimeAxis, string> = {
  DELIVERY_DATE: 'wg dnia dostawy',
  ORDER_DATE: 'wg dnia zakupu',
};

/** Pełne zdanie do tooltipa — tam, gdzie skrót na znaczniku to za mało. */
export const VALUE_METRIC_HINT: Record<ValueMetric, string> = {
  LIST_VALUE:
    'Suma cen katalogowych pozycji, PRZED zniżką szkolną. To NIE jest kwota, która wpłynęła — ' +
    'szkoły z promocją zapłaciły mniej. Metryka służy porównywalności ofert, nie rozliczeniom.',
  PAID_AMOUNT:
    'Kwota faktycznie pobrana od kupującego. Dostępna wyłącznie na tym ziarnie, na którym ' +
    'źródło ją przechowuje — nie jest rozdzielana na pozycje bez zaakceptowanego modelu alokacji.',
};

export const VOLUME_UNIT_HINT: Record<VolumeUnit, string> = {
  ORDER_COUNT: 'Liczba różnych zamówień. Jedno zamówienie z pięcioma daniami liczy się raz.',
  PORTION_COUNT: 'Suma ilości z pozycji — ile porcji realnie wydano.',
  UNIQUE_BUYERS: 'Liczba różnych kupujących w oknie.',
  ORDER_ITEM_ROWS:
    'Liczba wierszy pozycji zamówienia. To NIE jest liczba zamówień ani liczba porcji — ' +
    'mylenie tych trzech było defektem starego panelu.',
};

export const TIME_AXIS_HINT: Record<TimeAxis, string> = {
  DELIVERY_DATE:
    'Dzień realizacji dostawy. Kanoniczna oś wyników sprzedaży (ADR-003) — sprzedaż przypisujemy ' +
    'do dnia, w którym posiłek trafił do ucznia.',
  ORDER_DATE:
    'Dzień złożenia zamówienia. Osobny wymiar, odpowiada na pytanie „kiedy ludzie zamawiają", ' +
    'a nie „ile sprzedano danego dnia".',
};

/**
 * Jednowierszowy opis podstawy, gotowy pod liczbę albo w nagłówek rankingu.
 * Pusty string, gdy nie podano ani jednego wymiaru — wtedy komponent po prostu nic nie rysuje.
 */
export function describeBasis(basis: MetricBasis): string {
  const części = [
    basis.value ? VALUE_METRIC_LABEL[basis.value] : null,
    basis.volume ? VOLUME_UNIT_LABEL[basis.volume] : null,
    basis.time ? TIME_AXIS_LABEL[basis.time] : null,
  ].filter(Boolean);
  return części.join(' · ');
}

/**
 * Czy metryka pieniężna wolno nazwać „przychodem" lub „obrotem".
 *
 * Odpowiedź brzmi NIE dla wartości katalogowej — i to nie jest przesada językowa.
 * Nazwanie sumy cennikowej „obrotem" stawia ją w jednym rzędzie z „Sumą transakcji" panelu,
 * która liczy kwoty pobrane. Zestawienie tych dwóch liczb obok siebie sugeruje różnicę
 * w danych tam, gdzie jest różnica w definicji.
 */
export function moznaNazwacPrzychodem(value: ValueMetric): boolean {
  return value === 'PAID_AMOUNT';
}
